import { Injectable, computed, inject, signal } from "@angular/core";
import { UtilityService } from '@eforall/common';
import { DbDomainData } from "@interfaces";
import { FuncService } from "../func/func.service.gen";
import { domainDataEquality } from "./domain-data-equality";

export interface DomainData {
	companyTypes: { companyTypeId: number, name: string }[],
	companyTypesMap: { [companyTypeId: number]: { name: string } },
	contactMethods: { contactMethodId: number, name: string, isDefault: boolean }[],
	contactMethodMap: { [contactMethodId: number]: { name: string, isDefault: boolean } },
	accApplicationStatuses: { accApplicationStatusId: number, name: string, desc: string, isOpen: boolean, order: number }[],
	accApplicationStatusesMap: { [accApplicationStatusId: number]: { name: string, desc: string, isOpen: boolean, order: number } },
	contactRequestCategories: { requestCategoryId: number, name: string }[],
	contactRequestCategoriesMap: { [requestCategoryId: number]: { name: string } },
	volunteerStatuses: { volunteerStatusId: number, name: string, desc: string }[],
	volunteerStatusesMap: { [volunteerStatusId: number]: { name: string, desc: string } },
	engagementPurposes: { purposeId: number, name: string }[],
	engagementPurposesMap: { [purposeId: number]: { name: string } },
	noteCategories: { noteCategoryId: number, name: string }[],
	industries: { industryId: number, name: string }[],
	industriesMap: { [industryId: number]: { name: string } },
	employeeStatuses: { employeeStatusId: number, name: string }[],
	employeeStatusesMap: { [employeeStatusId: number]: { name: string } },
	expertises: { expertiseId: number, name: string }[],
	expertisesMap: { [expertiseId: number]: { name: string } },
	webLinkTypes: { webLinkTypeId: number, name: string, order: number }[],
	webLinkTypesMap: { [webLinkTypeId: number]: { name: string, order: number } },
	phoneTypes: { phoneTypeId: number, name: string }[],
	phoneTypesMap: { [phoneTypeId: number]: { name: string } },
	topics: { topicId: number, name: string, order: number, enabled: boolean, needsSpecialist: boolean }[],
	topicsMap: { [topicId: number]: { name: string } },
	questionnaireLearn: { questionnaireLearnId: number, name: string }[],
	questionnaireLearnMap: { [questionnaireLearnId: number]: { name: string } },
	engagementExternalRoles: { engagementExternalRoleId: number, name: string }[],
	engagementExternalRolesMap: { [engagementExternalRoleId: number]: { name: string } },
	staffRoles: { staffRoleId: number, name: string }[],
	staffRolesMap: { [staffRoleId: number]: { name: string } },
	accGoalCategories: { accGoalCategoryId: number, name: string, order: number }[],
	accGoalCategoriesMap: { [accGoalCategoryId: number]: { name: string, order: number } },
	dropoutReasons: { dropoutReasonId: number, name: string, desc: string | undefined, isActive: boolean, isVentureReason: boolean, isMentorReason: boolean }[],
	dropoutReasonsMap: { [dropoutReasonId: number]: { name: string, desc: string | undefined, isActive: boolean, isVentureReason: boolean, isMentorReason: boolean } },
	lmiBands: { lmiBandId: number, name: string }[],
	lmiBandsMap: { [lmiBandId: number]: { name: string } },
}

const EMPTY_DOMAIN_DATA: DbDomainData = {
	asOfUtc: 0,
	companyTypes: [],
	contactMethods: [],
	accApplicationStatuses: [],
	contactRequestCategories: [],
	volunteerStatuses: [],
	engagementPurposes: [],
	noteCategories: [],
	industries: [],
	employeeStatuses: [],
	expertises: [],
	webLinkTypes: [],
	phoneTypes: [],
	topics: [],
	questionnaireLearn: [],
	engagementExternalRoles: [],
	staffRoles: [],
	accGoalCategories: [],
	dropoutReasons: [],
	lmiBands: [],
};

@Injectable({ providedIn: 'root' })
export class DomainDataService {

	private readonly func = inject(FuncService);
	private readonly util = inject(UtilityService);

	private readonly _dbData = signal<DbDomainData>(EMPTY_DOMAIN_DATA, { equal: domainDataEquality });
	public readonly data = computed(() => {
		const db = this._dbData();

		const data: DomainData = {
			companyTypes: db.companyTypes.map(v => ({ companyTypeId: v.id, name: v.name })),
			companyTypesMap: {},
			contactMethods: db.contactMethods.map(v => ({ contactMethodId: v.id, name: v.name, isDefault: v.isDefault })),
			contactMethodMap: {},
			accApplicationStatuses: db.accApplicationStatuses.map(v => ({ accApplicationStatusId: v.id, name: v.name, desc: v.desc, isOpen: v.isOpen, order: v.order })),
			accApplicationStatusesMap: {},
			contactRequestCategories: db.contactRequestCategories.map(v => ({ requestCategoryId: v.id, name: v.name })),
			contactRequestCategoriesMap: {},
			volunteerStatuses: db.volunteerStatuses.map(v => ({ volunteerStatusId: v.id, name: v.name, desc: v.desc })),
			volunteerStatusesMap: {},
			engagementPurposes: db.engagementPurposes.map(v => ({ purposeId: v.id, name: v.name })),
			engagementPurposesMap: {},
			noteCategories: db.noteCategories.map(v => ({ noteCategoryId: v.id, name: v.name })),
			industries: db.industries.map(v => ({ industryId: v.id, name: v.name })),
			industriesMap: {},
			employeeStatuses: db.employeeStatuses.map(v => ({ employeeStatusId: v.id, name: v.name, code: v.code })),
			employeeStatusesMap: {},
			expertises: db.expertises ? db.expertises.map(v => ({ expertiseId: v.id, name: v.name })) : [],
			expertisesMap: {},
			webLinkTypes: db.webLinkTypes.map(v => ({ webLinkTypeId: v.id, name: v.name, order: v.order })),
			webLinkTypesMap: {},
			phoneTypes: db.phoneTypes ? db.phoneTypes.map(v => ({ phoneTypeId: v.id, name: v.name })) : [],
			phoneTypesMap: {},
			topics: db.topics.map(v => ({ topicId: v.id, name: v.name, order: v.order, enabled: v.enabled, needsSpecialist: v.needsSpecialist })),
			topicsMap: {},
			questionnaireLearn: db.questionnaireLearn.map(v => ({ questionnaireLearnId: v.id, name: v.name })),
			questionnaireLearnMap: {},
			engagementExternalRoles: db.engagementExternalRoles.map(v => ({ engagementExternalRoleId: v.id, name: v.name })),
			engagementExternalRolesMap: {},
			staffRoles: db.staffRoles.map(v => ({ staffRoleId: v.id, name: v.name })),
			staffRolesMap: {},
			accGoalCategories: db.accGoalCategories.map(v => ({ accGoalCategoryId: v.id, name: v.name, order: v.order })),
			accGoalCategoriesMap: {},
			dropoutReasons: db.dropoutReasons.map(v => ({ dropoutReasonId: v.id, name: v.name, desc: v.desc, isActive: v.isActive, isVentureReason: v.isVentureReason, isMentorReason: v.isMentorReason })),
			dropoutReasonsMap: {},
			lmiBands: db.lmiBands.map(v => ({ lmiBandId: v.id, name: v.name })),
			lmiBandsMap: {},
		};

		return {
			...data,
			companyTypesMap: this.util.array.toMap(data.companyTypes, v => v.companyTypeId),
			contactMethodMap: this.util.array.toMap(data.contactMethods, v => v.contactMethodId),
			accApplicationStatusesMap: this.util.array.toMap(data.accApplicationStatuses, v => v.accApplicationStatusId),
			contactRequestCategoriesMap: this.util.array.toMap(data.contactRequestCategories, v => v.requestCategoryId),
			volunteerStatusesMap: this.util.array.toMap(data.volunteerStatuses, v => v.volunteerStatusId),
			engagementPurposesMap: this.util.array.toMap(data.engagementPurposes, v => v.purposeId),
			noteCategoryMap: this.util.array.toMap(data.noteCategories, v => v.noteCategoryId),
			industriesMap: this.util.array.toMap(data.industries, v => v.industryId),
			employeeStatusesMap: this.util.array.toMap(data.employeeStatuses, v => v.employeeStatusId),
			expertisesMap: this.util.array.toMap(data.expertises, v => v.expertiseId),
			webLinkTypesMap: this.util.array.toMap(data.webLinkTypes, v => v.webLinkTypeId),
			phoneTypesMap: this.util.array.toMap(data.phoneTypes, v => v.phoneTypeId),
			topicsMap: this.util.array.toMap(data.topics, v => v.topicId),
			questionnaireLearnMap: this.util.array.toMap(data.questionnaireLearn, v => v.questionnaireLearnId),
			engagementExternalRolesMap: this.util.array.toMap(data.engagementExternalRoles, v => v.engagementExternalRoleId),
			staffRolesMap: this.util.array.toMap(data.staffRoles, v => v.staffRoleId),
			accGoalCategoriesMap: this.util.array.toMap(data.accGoalCategories, v => v.accGoalCategoryId),
			dropoutReasonsMap: this.util.array.toMap(data.dropoutReasons, v => v.dropoutReasonId),
			lmiBandsMap: this.util.array.toMap(data.lmiBands, v => v.lmiBandId),
		};

	});


	public async loadDomainData(): Promise<void> {
		this._dbData.set(await this.func.getDomainData());
	}


	public async clearDomainData(): Promise<void> {
		this._dbData.set(EMPTY_DOMAIN_DATA);
	}

}